import React from 'react';
import { useTheme } from '../../../../context/ThemeContext';
import { useAuthContext } from '../../../../hooks/useAuthContext';
import { BriefcaseIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import InlineEditableField from '../../../common/InlineEditableField';
import LocationSearchFieldV2 from '../../../common/LocationSearchFieldV2';
import { parseDisplayName } from '../../../../utils/nameUtils';
import { updateUserName } from '../../../../services/firebase/user-profile';
import { useProfileCompletion } from '../../../../hooks/useProfileCompletion';

const GeneralInformationSection = ({
  profileData,
  onSave,
  isLoading = false
}) => {
  const { isDarkMode } = useTheme();
  const { user } = useAuthContext();
  const { missingFields } = useProfileCompletion();

  // Parse names from user's displayName as source of truth
  const { firstName, lastName } = parseDisplayName(user?.displayName);
  
  // Use parsed names, falling back to profileData if displayName is empty
  const displayFirstName = firstName || profileData.firstName || '';
  const displayLastName = lastName || profileData.lastName || '';

  const validateLinkedIn = (url) => {
    if (!url) return true; // Allow empty
    const linkedinRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
    return linkedinRegex.test(url) || url.includes('linkedin.com');
  };

  const formatLinkedInUrl = (url) => {
    if (!url) return '';
    if (!/^https?:\/\//i.test(url)) {
      return `https://linkedin.com/in/${url}`;
    }
    return url;
  };

  const validateWebsite = (url) => {
    if (!url) return true; // Allow empty
    const urlRegex = /^https?:\/\/.+/;
    return urlRegex.test(url) || !url.includes('http');
  };

  const formatWebsiteUrl = (url) => {
    if (!url) return '';
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`;
    }
    return url;
  };

  const handleSave = async (field, value, locationData = null) => {
    try {
      // Handle name fields specially to update displayName
      if (field === 'firstName' || field === 'lastName') {
        // Validate name input
        if (!value || !value.trim()) {
          throw new Error(`${field === 'firstName' ? 'First' : 'Last'} name cannot be empty`);
        }
        
        const trimmedValue = value.trim();
        const currentFirstName = field === 'firstName' ? trimmedValue : displayFirstName;
        const currentLastName = field === 'lastName' ? trimmedValue : displayLastName;
        
        // Validate that we have at least one name
        if (!currentFirstName && !currentLastName) {
          throw new Error('You must provide at least a first name or last name');
        }
        
        // Update name using the specialized function with retry logic
        let retryCount = 0;
        const maxRetries = 2;
        
        while (retryCount <= maxRetries) {
          try {
            const result = await updateUserName(user.uid, currentFirstName, currentLastName);
            if (!result.success) {
              throw new Error(result.error);
            }
            break; // Success, exit retry loop
          } catch (nameError) {
            retryCount++;
            if (retryCount > maxRetries) {
              throw nameError;
            }
            // Wait before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        
        // Also update the profile data through the normal flow
        await onSave(field, trimmedValue, locationData);
      } else {
        await onSave(field, value, locationData);
      }
    } catch (error) {
      console.error(`Error saving ${field}:`, error);
      
      // Provide user-friendly error messages
      let userMessage = error.message;
      
      if (error.message.includes('network') || error.message.includes('offline')) {
        userMessage = 'Network error. Please check your connection and try again.';
      } else if (error.message.includes('permission') || error.message.includes('unauthorized')) {
        userMessage = 'Permission denied. Please log in again.';
      } else if (error.message.includes('auth')) {
        userMessage = 'Authentication error. Please log in again.';
      }
      
      throw new Error(userMessage);
    }
  };

  // Check if any fields in this section are missing
  const sectionMissingFields = missingFields.filter(field => 
    ['firstName', 'lastName', 'currentRole', 'companyName', 'location', 'linkedin'].includes(field)
  );
  const hasIncompleteFields = sectionMissingFields.length > 0;

  // Get field labels for missing fields
  const getMissingFieldLabels = () => {
    const fieldLabels = {
      firstName: 'First Name',
      lastName: 'Last Name',
      currentRole: 'Current Role',
      companyName: 'Company',
      location: 'Location',
      linkedin: 'LinkedIn Profile'
    };
    return sectionMissingFields.map(field => fieldLabels[field]).filter(Boolean);
  };

  return (
    <div id="general-info-section" className={`mb-8 transition-all duration-300 ${hasIncompleteFields ? (isDarkMode ? 'border-2 border-purple-500/50 ring-2 ring-purple-500/20 rounded-lg p-6' : 'border-2 border-purple-500/50 ring-2 ring-purple-500/10 rounded-lg p-6') : ''}`}>
      
      {/* Completion Indicator */}
      {hasIncompleteFields && (
        <div className={`mb-6 p-4 rounded-lg ${isDarkMode ? 'bg-purple-900/20 border border-purple-500/30' : 'bg-purple-50 border border-purple-200'}`}>
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            <div className="flex-1">
              <p className={`text-sm font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-700'} mb-2`}>
                Complete your profile to improve networking opportunities
              </p>
              <p className={`text-xs ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`}>
                Missing: {getMissingFieldLabels().join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Section Header */}
      <div className="mb-6">
        <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'} mb-2`}>
          Essential Information
        </h2>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Your core networking details that help others connect with you professionally
        </p>
      </div>

      {/* Fields Container with Beautiful Styling */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800/50' : 'bg-white'} 
        rounded-xl border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} 
        p-6 sm:p-8 space-y-8 shadow-sm
      `}>
        {/* Name Fields */}
        <div className="space-y-6">
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wide`}>
            Personal Details
          </h3>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <InlineEditableField
              label="First Name"
              value={displayFirstName}
              onSave={(value) => handleSave('firstName', value)}
              placeholder="Enter your first name"
              required
              disabled={isLoading}
              inputProps={{
                autoComplete: 'given-name'
              }}
            />
            
            <InlineEditableField
              label="Last Name"
              value={displayLastName}
              onSave={(value) => handleSave('lastName', value)}
              placeholder="Enter your last name"
              required
              disabled={isLoading}
              inputProps={{
                autoComplete: 'family-name'
              }}
            />
          </div>
        </div>

        {/* Professional Information */}
        <div className="space-y-6">
          <div className="flex items-center">
            <BriefcaseIcon className={`h-5 w-5 mr-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wide`}>
              Professional Details
            </h3>
          </div>
          <div className="space-y-6">
            <InlineEditableField
              label="Current Role"
              value={profileData.currentRole}
              onSave={(value) => handleSave('currentRole', value)}
              placeholder="e.g., Software Engineer, Product Manager"
              required
              disabled={isLoading}
              inputProps={{
                autoComplete: 'organization-title'
              }}
            />

            <InlineEditableField
              label="Company"
              value={profileData.companyName}
              onSave={(value) => handleSave('companyName', value)}
              placeholder="Enter your company name"
              required
              disabled={isLoading}
              inputProps={{
                autoComplete: 'organization'
              }}
            />

            <LocationSearchFieldV2
              value={profileData.location || ''}
              onLocationSelect={(locationDetails, suggestion) => {
                console.log('🏠 Location selected:', locationDetails);
                
                // Format location string for display (city, state, country)
                const locationString = [
                  locationDetails.city,
                  locationDetails.state,
                  locationDetails.country
                ].filter(Boolean).join(', ');
                
                // Convert LocationDetails class instance to plain object for Firestore
                const plainLocationData = {
                  id: locationDetails.id,
                  city: locationDetails.city,
                  state: locationDetails.state,
                  country: locationDetails.country,
                  coordinates: locationDetails.coordinates,
                  formattedAddress: locationDetails.formattedAddress,
                  timestamp: new Date()
                };
                
                console.log('🏠 Formatted location string:', locationString);
                console.log('🏠 Plain location data:', plainLocationData);
                
                handleSave('location', locationString, plainLocationData);
              }}
              placeholder="e.g., San Francisco, CA"
              required
              disabled={isLoading}
              label="Home City"
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-6">
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} uppercase tracking-wide`}>
            Professional Links
          </h3>
          <div className="space-y-6">
            <InlineEditableField
              label="LinkedIn Profile"
              value={profileData.basicInfo?.linkedinUrl || ''}
              onSave={(value) => handleSave('basicInfo.linkedinUrl', formatLinkedInUrl(value))}
              type="url"
              placeholder="https://linkedin.com/in/yourprofile"
              validation={validateLinkedIn}
              required
              disabled={isLoading}
              inputProps={{
                autoComplete: 'url'
              }}
            />

            <InlineEditableField
              label="Personal Website"
              value={profileData.website}
              onSave={(value) => handleSave('website', formatWebsiteUrl(value))}
              type="url"
              placeholder="https://yourwebsite.com"
              validation={validateWebsite}
              disabled={isLoading}
              inputProps={{
                autoComplete: 'url'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralInformationSection;